import evasGoSrc from '@/assets/evasgo_1.wav'
import insertionToSrc from '@/assets/insertion2cecum_1.wav'
import finishSrc from '@/assets/finish_1.wav'

const STORAGE_KEY = 'evas2.voice-command-cues.v1'

export const VOICE_COMMAND_CUE_SOURCES = Object.freeze({
  evas_go: evasGoSrc,
  insertion_to: insertionToSrc,
  evas_finish: finishSrc,
})

function browserSessionStorage() {
  try {
    return window.sessionStorage
  } catch (err) {
    return null
  }
}

/** 同時收斂同步例外與 rejected promise；任何媒體操作都只回 boolean。 */
function mediaOperation(operation) {
  try {
    return Promise.resolve(operation()).then(() => true).catch(() => false)
  } catch (err) {
    return Promise.resolve(false)
  }
}

/**
 * 已受理語音指令的短音效。
 *
 * 播放失敗是瀏覽器能力降級，不可回頭改變 server 已接受的醫療流程；所有 media
 * promise 都在本服務內收斂。去重先於 play，避免同一事件因重送或播放失敗被重播。
 */
export class VoiceCommandCues {
  constructor({ createAudio, storage } = {}) {
    this.createAudio = createAudio || ((src) => new Audio(src))
    this.storage = storage === undefined ? browserSessionStorage() : storage
    this.players = {}
    this.volatilePlayed = new Set()
    this.unlocking = null
    this.unlocked = false
  }

  player(command) {
    try {
      if (!this.players[command]) {
        const audio = this.createAudio(VOICE_COMMAND_CUE_SOURCES[command])
        // preload 也是 HTMLMediaElement property；自訂瀏覽器殼層可能在 setter 丟錯。
        try {
          audio.preload = 'auto'
        } catch (err) {
          // 預載失敗不影響稍後直接 play。
        }
        this.players[command] = audio
      }
      return this.players[command]
    } catch (err) {
      return null
    }
  }

  playedKeys() {
    if (!this.storage) return []
    try {
      const value = JSON.parse(this.storage.getItem(STORAGE_KEY) || '[]')
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : []
    } catch (err) {
      return []
    }
  }

  markPlayed(key) {
    this.volatilePlayed.add(key)
    if (!this.storage) return
    try {
      const keys = this.playedKeys().filter((item) => item !== key)
      keys.push(key)
      this.storage.setItem(STORAGE_KEY, JSON.stringify(keys))
    } catch (err) {
      // Safari 無痕模式或 storage quota 滿時，仍保留本頁記憶體去重。
    }
  }

  hasPlayed(key) {
    return this.volatilePlayed.has(key) || this.playedKeys().includes(key)
  }

  /**
   * 必須由真實 user gesture 同步呼叫。失敗不鎖死，下一次手勢可再嘗試。
   */
  unlock() {
    if (this.unlocked) return Promise.resolve(true)
    if (this.unlocking) return this.unlocking

    const attempts = Object.keys(VOICE_COMMAND_CUE_SOURCES).map((command) => {
      const audio = this.player(command)
      if (!audio) return Promise.resolve(false)
      const started = mediaOperation(() => {
        // 必須與 play 同在 user gesture 的同步 call stack；不可先 await setter。
        audio.muted = true
        audio.currentTime = 0
        return audio.play()
      })
      // 每一支都先自行收斂，等全部完成再開放重試，避免失敗的一支讓另一支
      // 還在 pending 時就重入、交錯 pause/play。
      return started.then(async (ok) => {
        const stopped = ok
          ? await mediaOperation(() => {
            audio.pause()
            audio.currentTime = 0
          })
          : true
        const unmuted = await mediaOperation(() => {
          audio.muted = false
        })
        return ok && stopped && unmuted
      }).catch(() => false)
    })

    this.unlocking = Promise.all(attempts)
      .then((results) => {
        this.unlocked = results.every(Boolean)
        return this.unlocked
      })
      .finally(() => {
        this.unlocking = null
      })
    return this.unlocking
  }

  /** 只接受帶穩定 session + seq 的語音 GO / insertion / finish；手動永遠不播放。 */
  playAccepted({ sessionId, eventId, command, source } = {}) {
    const eventIdType = typeof eventId
    const hasEventId = (eventIdType === 'number' || eventIdType === 'string')
      && !(eventIdType === 'string' && eventId.trim() === '')
    const seq = Number(eventId)
    if (
      source === 'manual'
      || !Object.prototype.hasOwnProperty.call(VOICE_COMMAND_CUE_SOURCES, command)
      || !String(sessionId == null ? '' : sessionId).trim()
      || !hasEventId
      || !Number.isInteger(seq)
      || seq <= 0
    ) return Promise.resolve(false)

    const key = `${String(sessionId)}:${seq}:${command}`
    if (this.hasPlayed(key)) return Promise.resolve(false)
    this.markPlayed(key)

    const audio = this.player(command)
    if (!audio) return Promise.resolve(false)

    const play = () => mediaOperation(() => {
      audio.muted = false
      audio.currentTime = 0
      return audio.play()
    })

    // 預解鎖尚未完成時先等它收尾，否則它稍後的 pause() 可能截斷真正提示音。
    return this.unlocking ? this.unlocking.then(play).catch(() => false) : play()
  }
}

export const voiceCommandCues = new VoiceCommandCues()
